import { useState } from "react";
import { Search, Sparkles, Disc3 } from "lucide-react";

const EXAMPLES = {
  search: [
    "Recreate 1960s Ethiopian jazz",
    "Punchy compressor for reggae drums",
    "Lush reverb for dream pop vocals",
    "Acid bassline synth for house",
    "Tape saturation for lo-fi hip hop",
    "Vintage EQ for a Motown mix",
  ],
  match: [
    "Amy Winehouse — Back to Black",
    "Daft Punk — Random Access Memories",
    "Radiohead — In Rainbows",
    "Dr. Dre — 2001",
    "Tame Impala — Currents",
    "Bob Marley — Exodus",
  ],
};

export const ModeToggle = ({ mode, onChange, disabled }) => (
  <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1">
    {[
      { key: "search", label: "Find Plugins", Icon: Sparkles },
      { key: "match", label: "Sound Match", Icon: Disc3 },
    ].map(({ key, label, Icon }) => (
      <button
        key={key}
        type="button"
        data-testid={`mode-toggle-${key}`}
        disabled={disabled}
        onClick={() => onChange(key)}
        className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors duration-200 ${
          mode === key ? "bg-cyan-500 text-black" : "text-white/50 hover:text-white"
        }`}
      >
        <Icon className="h-3.5 w-3.5" /> {label}
      </button>
    ))}
  </div>
);

export const SearchBar = ({ initial = "", mode = "search", onSearch, compact }) => {
  const [value, setValue] = useState(initial);
  const submit = (e) => {
    e.preventDefault();
    onSearch(value);
  };
  return (
    <form onSubmit={submit} className="beam-wrap w-full">
      <div className="flex items-center gap-3 rounded-[13px] bg-[#111114] px-5">
        {mode === "match" ? (
          <Disc3 className="h-5 w-5 shrink-0 text-cyan-400" />
        ) : (
          <Sparkles className="h-5 w-5 shrink-0 text-cyan-400" />
        )}
        <input
          data-testid="search-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={mode === "match" ? "Paste a song or artist to reverse-engineer..." : "Describe the sound you're chasing..."}
          className={`w-full bg-transparent text-white placeholder-white/30 outline-none ${compact ? "py-3.5 text-base" : "py-5 text-lg"}`}
          autoFocus={!compact}
        />
        <button
          type="submit"
          data-testid="search-submit-btn"
          className="my-2 flex shrink-0 items-center gap-2 rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-black transition-colors duration-200 hover:bg-cyan-400"
        >
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">{mode === "match" ? "Match" : "Search"}</span>
        </button>
      </div>
    </form>
  );
};

export const HeroSearch = ({ mode, onModeChange, onSearch }) => (
  <main className="relative z-10 flex min-h-[calc(100vh-65px)] flex-col items-center justify-center px-6 pb-24">
    <div className="w-full max-w-2xl">
      <div className="anim-fade-up text-center">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">The producer's plugin oracle</div>
        {mode === "match" ? (
          <h1 className="mt-4 font-heading text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl">
            Name the record.
            <br />
            <span className="text-white/40">Get the plugin chain.</span>
          </h1>
        ) : (
          <h1 className="mt-4 font-heading text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl">
            Describe the sound.
            <br />
            <span className="text-white/40">We'll find the plugin.</span>
          </h1>
        )}
        <p className="mx-auto mt-5 max-w-md text-base text-white/50">
          {mode === "match"
            ? "Deconstruct the record's production DNA and build the chain to recreate it."
            : "Ask like a producer, not a search engine. Genres, eras, artists, textures — describe it your way."}
        </p>
      </div>

      <div className="anim-fade-up mt-8 flex justify-center" style={{ animationDelay: "0.05s" }}>
        <ModeToggle mode={mode} onChange={onModeChange} />
      </div>

      <div className="anim-fade-up mt-6" style={{ animationDelay: "0.1s" }}>
        <SearchBar key={mode} mode={mode} onSearch={onSearch} />
      </div>

      <div className="anim-fade-up mt-6 flex flex-wrap justify-center gap-2" style={{ animationDelay: "0.2s" }}>
        {EXAMPLES[mode].map((ex) => (
          <button
            key={ex}
            data-testid={`example-chip-${ex.slice(0, 12).replace(/\s+/g, "-")}`}
            onClick={() => onSearch(ex)}
            className="rounded-full bg-white/5 px-4 py-2 text-xs text-white/60 transition-colors duration-200 hover:bg-white/10 hover:text-white"
          >
            {ex}
          </button>
        ))}
      </div>
    </div>
  </main>
);
